import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Switch,
} from "@material-ui/core";
import { useTranslation } from "../hooks/useTranslation";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { getRuleIconComponent, SecurityIcon, CategoryIcon } from "../utils/ruleIcons";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    gap: 24,
    minHeight: 360,
  },
  leftPanel: {
    width: 220,
    flexShrink: 0,
    borderRight: "1px solid var(--ikaro-gray-medium)",
    paddingRight: 16,
  },
  leftTitle: {
    fontWeight: 600,
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark, #555)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 12,
  },
  serviceItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    marginBottom: 4,
    borderRadius: 8,
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    "&:hover": {
      backgroundColor: "var(--ikaro-gray-light, #f5f5f5)",
    },
  },
  serviceItemSelected: {
    borderLeftColor: "#000",
    backgroundColor: "rgba(0,0,0,0.04)",
    "& $serviceName": { fontWeight: 700 },
  },
  serviceName: {
    flex: 1,
    fontSize: "0.875rem",
  },
  serviceBadge: {
    minWidth: 24,
    height: 24,
    borderRadius: "50%",
    backgroundColor: "#000",
    color: "#fff",
    fontSize: "0.75rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    gap: 16,
  },
  submenu: {
    width: 200,
    flexShrink: 0,
    borderRight: "1px solid var(--ikaro-gray-medium)",
    paddingRight: 12,
  },
  submenuItem: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    marginBottom: 2,
    borderRadius: 8,
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    "&:hover": {
      backgroundColor: "var(--ikaro-gray-light, #f5f5f5)",
    },
  },
  submenuItemSelected: {
    borderLeftColor: "#000",
    backgroundColor: "rgba(0,0,0,0.04)",
    "& $submenuItemName": { fontWeight: 600 },
  },
  submenuItemName: {
    flex: 1,
    fontSize: "0.8125rem",
  },
  submenuBadge: {
    minWidth: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: "#000",
    color: "#fff",
    fontSize: "0.7rem",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rightContent: {
    flex: 1,
    minWidth: 0,
  },
  associateAllRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 12,
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0)",
    background: "unset",
    borderRadius: 8,
  },
  ruleRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    marginBottom: 8,
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: "8px",
    backgroundColor: "var(--ikaro-bg-surface)",
  },
  ruleIconWrap: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    flexShrink: 0,
  },
  ruleIconService: {
    backgroundColor: "rgba(255, 152, 0, 0.15)",
    color: "var(--ikaro-orange-primary, #f57c00)",
  },
  ruleIconProduct: {
    backgroundColor: "rgba(76, 175, 80, 0.15)",
    color: "#4caf50",
  },
  ruleContent: {
    flex: 1,
    minWidth: 0,
  },
  rulePrimary: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  ruleSecondary: {
    fontSize: "0.75rem",
    color: "var(--ikaro-gray-dark, #555)",
    marginTop: 2,
  },
  sectionLabel: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: "var(--ikaro-gray-dark, #333)",
    marginTop: 16,
    marginBottom: 8,
  },
  emptyState: {
    padding: 24,
    textAlign: "center",
    color: "var(--ikaro-gray-dark, #555)",
    fontSize: "0.875rem",
  },
}));

const ServiceTree = ({
  services = [],
  userServices = [],
  userProducts = [],
  selectedUser,
  onServiceToggle,
  onRuleToggle,
  onProductRuleToggle,
  hasRole,
  readOnly = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const [selectedServiceId, setSelectedServiceId] = useState(
    services[0]?.service_id ?? null,
  );
  const [selectedSubmenu, setSelectedSubmenu] = useState("globais");

  const selectedService = services.find(
    (s) => String(s.service_id) === String(selectedServiceId),
  );
  const userService = selectedService
    ? userServices.find(
        (s) => String(s.service_id) === String(selectedService.service_id),
      )
    : null;
  const rules = selectedService?.rules || [];
  const products = selectedService?.products || [];

  // Ao trocar de serviço, definir submenu válido (globais ou primeiro produto com regras)
  useEffect(() => {
    if (!selectedService) return;
    const hasGlobais = rules.length > 0;
    const firstProduct = products.find((p) => (p.rules || []).length > 0);
    setSelectedSubmenu(
      hasGlobais ? "globais" : (firstProduct?.product_code ?? "globais"),
    );
  }, [selectedServiceId]);

  const canManage = !readOnly && hasRole("USER_MANAGER", "user_manager");

  const isServiceRuleAssociated = (userSvc, ruleId) =>
    userSvc?.rules?.some((r) => String(r.rule_id) === String(ruleId));

  /**
   * users/rules devolve TODAS as regras do catálogo com assigned true/false.
   * Só contar como associada se assigned === true (não basta existir rule_id na lista).
   * Modal "criar usuário" envia só { rule_id } sem assigned — aí o match por id basta.
   */
  const isProductRuleAssociated = (userProduct, ruleId) =>
    userProduct?.rules?.some((r) => {
      const idMatch =
        r.rule_id === ruleId || String(r.rule_id) === String(ruleId);
      if (!idMatch) return false;
      if (r.assigned === undefined) return true;
      return r.assigned === true;
    });

  return (
    <Box className={classes.root}>
      {/* Painel esquerdo: SERVIÇOS */}
      <Box className={classes.leftPanel}>
        <Typography className={classes.leftTitle}>
          {t("serviceTree.services")}
        </Typography>
        {services.map((service) => {
          const count = (service.products || []).length;
          const isSelected =
            String(service.service_id) === String(selectedServiceId);
          return (
            <Box
              key={service.service_id}
              className={`${classes.serviceItem} ${
                isSelected ? classes.serviceItemSelected : ""
              }`}
              onClick={() => {
                setSelectedServiceId(service.service_id);
                setSelectedSubmenu("globais");
              }}
            >
              <Typography className={classes.serviceName}>
                {service.service_description || service.service_name}
              </Typography>
              <Box className={classes.serviceBadge}>{count}</Box>
            </Box>
          );
        })}
      </Box>

      {/* Painel direito: submenu (Globais + produtos) + conteúdo */}
      <Box className={classes.rightPanel}>
        {selectedService ? (
          <>
            {/* Submenu: Globais + listagem de produtos */}
            <Box className={classes.submenu}>
              <Typography
                className={classes.sectionLabel}
                style={{ marginTop: 0 }}
              >
                {t("serviceTree.products")}
              </Typography>
              {rules.length > 0 && (
                <Box
                  className={`${classes.submenuItem} ${
                    selectedSubmenu === "globais"
                      ? classes.submenuItemSelected
                      : ""
                  }`}
                  onClick={() => setSelectedSubmenu("globais")}
                >
                  <Typography className={classes.submenuItemName}>
                    {t("serviceTree.globals")}
                  </Typography>
                  <Box className={classes.submenuBadge}>{rules.length}</Box>
                </Box>
              )}
              {products.map((product) => {
                const productRules = product.rules || [];
                if (productRules.length === 0) return null;
                const isSelected = selectedSubmenu === product.product_code;
                return (
                  <Box
                    key={product.product_code}
                    className={`${classes.submenuItem} ${
                      isSelected ? classes.submenuItemSelected : ""
                    }`}
                    onClick={() => setSelectedSubmenu(product.product_code)}
                  >
                    <Typography className={classes.submenuItemName}>
                      {product.product_name}
                    </Typography>
                    <Box className={classes.submenuBadge}>
                      {productRules.length}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Conteúdo: regras do item selecionado (globais ou produto) */}
            <Box className={classes.rightContent}>
              {selectedSubmenu === "globais" && (
                <>
                  {rules.length === 0 ? (
                    <Typography className={classes.emptyState}>
                      {t("serviceTree.noGlobalPermissions")}
                    </Typography>
                  ) : (
                    <>
                      {selectedUser && canManage && (
                        <Box
                          className={classes.associateAllRow}
                          style={{ width: "100%" }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              await onServiceToggle(
                                selectedUser.id,
                                selectedService.service_id,
                                true,
                              );
                            }}
                            style={{
                              borderColor: "#2e7d32",
                              color: "#2e7d32",
                              backgroundColor: "transparent",
                            }}
                          >
                            {t("serviceTree.associateAll")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              await onServiceToggle(
                                selectedUser.id,
                                selectedService.service_id,
                                false,
                              );
                            }}
                            style={{
                              borderColor: "#c62828",
                              color: "#c62828",
                              backgroundColor: "transparent",
                            }}
                          >
                            {t("serviceTree.disassociateAll")}
                          </Button>
                        </Box>
                      )}
                      <Typography className={classes.sectionLabel}>
                        {t("serviceTree.generalPermissionsLabel")}
                      </Typography>
                      <List dense disablePadding>
                        {[...rules]
                          .sort((a, b) =>
                            (a.rule_name || "").localeCompare(
                              b.rule_name || "",
                              "pt",
                            ),
                          )
                          .map((rule) => (
                            <ListItem
                              key={rule.rule_id}
                              disableGutters
                              className={classes.ruleRow}
                            >
                              <Box
                                className={`${classes.ruleIconWrap} ${classes.ruleIconService}`}
                              >
                                {React.createElement(
                                  getRuleIconComponent(rule) || SecurityIcon,
                                  { fontSize: "small" },
                                )}
                              </Box>
                              <Box className={classes.ruleContent}>
                                <ListItemText
                                  primary={rule.rule_name}
                                  secondary={rule.rule_description || null}
                                  primaryTypographyProps={{
                                    className: classes.rulePrimary,
                                  }}
                                  secondaryTypographyProps={{
                                    className: classes.ruleSecondary,
                                  }}
                                />
                              </Box>
                              {readOnly ? (
                                <CheckCircleOutlineIcon
                                  color="primary"
                                  fontSize="small"
                                  style={{ opacity: 0.85 }}
                                />
                              ) : (
                                <Switch
                                  checked={isServiceRuleAssociated(
                                    userService,
                                    rule.rule_id,
                                  )}
                                  onChange={(e) =>
                                    selectedUser &&
                                    onRuleToggle(
                                      selectedUser.id,
                                      rule.rule_id,
                                      e.target.checked,
                                    )
                                  }
                                  color="primary"
                                  disabled={!canManage}
                                  size="small"
                                />
                              )}
                            </ListItem>
                          ))}
                      </List>
                    </>
                  )}
                </>
              )}

              {selectedSubmenu !== "globais" &&
                (() => {
                  const product = products.find(
                    (p) => p.product_code === selectedSubmenu,
                  );
                  if (!product) return null;
                  const userProduct = userProducts.find(
                    (p) => p.product_code === product.product_code,
                  );
                  const productRules = product.rules || [];
                  return (
                    <>
                      {selectedUser && canManage && productRules.length > 0 && (
                        <Box
                          className={classes.associateAllRow}
                          style={{ width: "100%" }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              for (const rule of productRules) {
                                await onProductRuleToggle(
                                  selectedUser.id,
                                  product.product_code,
                                  rule.rule_id,
                                  true,
                                );
                              }
                            }}
                            style={{
                              borderColor: "#2e7d32",
                              color: "#2e7d32",
                              backgroundColor: "transparent",
                            }}
                          >
                            {t("serviceTree.associateAll")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              for (const rule of productRules) {
                                await onProductRuleToggle(
                                  selectedUser.id,
                                  product.product_code,
                                  rule.rule_id,
                                  false,
                                );
                              }
                            }}
                            style={{
                              borderColor: "#c62828",
                              color: "#c62828",
                              backgroundColor: "transparent",
                            }}
                          >
                            {t("serviceTree.disassociateAll")}
                          </Button>
                        </Box>
                      )}
                      <Typography className={classes.sectionLabel}>
                        {product.product_name}
                      </Typography>
                      {productRules.length === 0 ? (
                        <Typography className={classes.emptyState}>
                          {t("serviceTree.noProductRules")}
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {[...productRules]
                            .sort((a, b) =>
                              (a.rule_name || "").localeCompare(
                                b.rule_name || "",
                                "pt",
                              ),
                            )
                            .map((rule) => (
                              <ListItem
                                key={`${product.product_code}-${rule.rule_id}`}
                                disableGutters
                                className={classes.ruleRow}
                              >
                                <Box
                                  className={`${classes.ruleIconWrap} ${classes.ruleIconProduct}`}
                                >
                                  {React.createElement(
                                    getRuleIconComponent(rule) || CategoryIcon,
                                    { fontSize: "small" },
                                  )}
                                </Box>
                                <Box className={classes.ruleContent}>
                                  <ListItemText
                                    primary={rule.rule_name}
                                    secondary={
                                      rule.rule_description ||
                                      t("serviceTree.ruleProductFallback")
                                    }
                                    primaryTypographyProps={{
                                      className: classes.rulePrimary,
                                    }}
                                    secondaryTypographyProps={{
                                      className: classes.ruleSecondary,
                                    }}
                                  />
                                </Box>
                                {readOnly ? (
                                  <CheckCircleOutlineIcon
                                    color="primary"
                                    fontSize="small"
                                    style={{ opacity: 0.85 }}
                                  />
                                ) : (
                                  <Switch
                                    checked={isProductRuleAssociated(
                                      userProduct,
                                      rule.rule_id,
                                    )}
                                    onChange={(e) =>
                                      selectedUser &&
                                      onProductRuleToggle(
                                        selectedUser.id,
                                        product.product_code,
                                        rule.rule_id,
                                        e.target.checked,
                                      )
                                    }
                                    color="primary"
                                    disabled={!canManage}
                                    size="small"
                                  />
                                )}
                              </ListItem>
                            ))}
                        </List>
                      )}
                    </>
                  );
                })()}

              {rules.length === 0 &&
                !products.some((p) => (p.rules || []).length > 0) && (
                  <Typography className={classes.emptyState}>
                    {t("serviceTree.noRulesOrProducts")}
                  </Typography>
                )}
            </Box>
          </>
        ) : (
          <Typography className={classes.emptyState}>
            {t("serviceTree.selectService")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ServiceTree;
